import { Button, Stack, Typography, Box } from "@mui/material";
import { useCallback, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Block } from "@mui/icons-material";

import { ICustomerWeNeed } from "../table/helpers";

import { createData } from "./helper";
import CustomerInfo from "./info";
import CustomerPaymentHistory from "./paymentHistory";
import CustomerSubscriptionHistory from "./subscriptionHistory";
import { createSubscriptionData } from "./subscriptionHistory/helper";

import DashboardContent from "@/components/app/DashboardContent";
import DashboardHeader from "@/components/app/DashboardHeader";
import ROUTE_CONSTANTS from "@/constants/route-constants";
import { IPaymentHistoryParams } from "@/api/types/payments";
import { IParams } from "@/types/params";
import { useCheckPermissionAccess } from "@/hooks/useCheckPermissionAccess";
import { useCustomerBlockAction, useCustomerById } from "@/api/hooks/useCustomer";
import { usePaymentHistory } from "@/api/hooks/usePaymentHistory";
import { useSubscriptions } from "@/api/hooks/useSubscriptions";
import { OperationAccess, PagesAccess } from "@/constants/enums/permissions";
import { MODAL_TYPES } from "@/types/modals";
import { modalActions } from "@/context/modals";
import { useUser } from "@/context/authContext";
import { ISubscriptionListParams, ISubscriptionItem } from "@/api/types/subscription";

const CustomerShow = () => {
    const { id } = useParams<IParams>();
    const user = useUser();
    const listAccess = useCheckPermissionAccess(PagesAccess.USER, OperationAccess.LIST);
    const editAccess = useCheckPermissionAccess(PagesAccess.USER, OperationAccess.UPDATE);

    const { data: customerData } = useCustomerById(Number(id));

    const [params, setParams] = useState<IPaymentHistoryParams>({
        skip: 0,
        take: 10,
        filter: { userId: Number(id) },
    } as IPaymentHistoryParams);

    const [subscriptionParams, setSubscriptionParams] = useState<ISubscriptionListParams>({
        skip: 0,
        take: 10,
        filter: { user: Number(id) },
    });

    const { data: paymentsData, isFetching, isLoading } = usePaymentHistory(params);
    const {
        data: subscriptionsData,
        isFetching: subscriptionsFetching,
        isLoading: subscriptionsLoading,
    } = useSubscriptions(subscriptionParams);
    const { mutate: blockCustomer, isPending: blockLoading } = useCustomerBlockAction();

    const handleBlock = async (user: ICustomerWeNeed, checked: boolean) => {
        const handleAction = async () => {
            blockCustomer({ id: user.id, body: { active: !checked } });
        };

        if (checked) {
            modalActions.addModal(MODAL_TYPES.CONFIRMATION_MODAL, {
                header: "Block Confirmation",
                text: "Are you sure to block this customer?",
                handleAction,
            });
        } else {
            handleAction();
        }
    };

    const handleChange = useCallback(({ page, rowsPerPage }: { page?: number, rowsPerPage?: number }) => {
        setParams((prev) => {
            return {
                ...prev,
                skip: Number(page) * Number(rowsPerPage),
                take: rowsPerPage || 10,
            };
        });
    }, []);

    const handleSubscriptionChange = useCallback(({ page, rowsPerPage }: { page?: number, rowsPerPage?: number }) => {
        setSubscriptionParams((prev) => {
            return {
                ...prev,
                skip: Number(page) * Number(rowsPerPage),
                take: rowsPerPage || 10,
            };
        });
    }, []);

    if (!customerData) return <h5>Oops! Something Went Wrong!</h5>;

    const payments = paymentsData?.data?.items || [];
    const totalItems = paymentsData?.data?.pagination.totalItems;
    const subscriptions = subscriptionsData?.data?.items || [];
    const subscriptionTotalItems = subscriptionsData?.data?.pagination?.totalItems || 0;
    const data = customerData?.data || {};

    return (
        <>
            <DashboardHeader
                title={`${data.firstName} ${data.lastName}`}
                titleSuffix={
                    <Stack direction={"row"} spacing={2}>
                        {editAccess && (
                            <>
                                <Button
                                    className={`block-btn ${data.state === "Enable" ? "" : "blocked"} `}
                                    color="error"
                                    disabled={(user?.id !== data.id && !editAccess) || blockLoading}
                                    endIcon={<Block />}
                                    variant="contained"
                                    onClick={() => handleBlock(data, data.state === "Enable")}
                                >
                                    Block
                                </Button>
                                <Button
                                    component={Link}
                                    to={ROUTE_CONSTANTS.USER_MANAGEMENT.CUSTOMERS.UPDATE.EDIT_BY_ID(data.id).ABSOLUTE}
                                    variant="contained"
                                >
                                    Edit Customer
                                </Button>
                            </>
                        )}
                        {listAccess && (
                            <Button component={Link} to={ROUTE_CONSTANTS.USER_MANAGEMENT.CUSTOMERS.ROOT.ABSOLUTE}>
                                Back To Customer List
                            </Button>
                        )}
                    </Stack>
                }
            />
            <DashboardContent>
                <CustomerInfo data={data} />
                <br />
                {/* <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: "text.primary" }}>
                        Payment History
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        View all payment transactions and billing records for this customer
                    </Typography>
                </Box>
                <CustomerPaymentHistory
                    data={payments.map((payment) => createData(payment))}
                    isFetching={isFetching}
                    isLoading={isLoading}
                    page={Number(params.skip) / Number(params.take)}
                    rowsPerPage={Number(params.take)}
                    totalItems={totalItems || 0}
                    onChange={handleChange}
                />
                <br /> */}
                <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: "text.primary" }}>
                        Subscription History
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        View all subscription records and payment transactions for this customer
                    </Typography>
                </Box>
                <CustomerSubscriptionHistory
                    data={subscriptions.map((subscription: ISubscriptionItem) => createSubscriptionData(subscription))}
                    isFetching={subscriptionsFetching}
                    isLoading={subscriptionsLoading}
                    page={Number(subscriptionParams.skip) / Number(subscriptionParams.take)}
                    rowsPerPage={Number(subscriptionParams.take)}
                    totalItems={subscriptionTotalItems || 0}
                    onChange={handleSubscriptionChange}
                />
            </DashboardContent>
        </>
    );
};

export default CustomerShow;
